import { createRouter as createTanStackRouter } from '@tanstack/react-router'
import { routeTree } from './routeTree.gen'
import { PageLoadingState } from './components/LoadingState'
import { track } from './lib/analytics'

export function getRouter() {
  const router = createTanStackRouter({
    routeTree,
    scrollRestoration: true,
    defaultPreload: 'intent',
    defaultPreloadStaleTime: 0,
    defaultPendingComponent: PageLoadingState,
    defaultPendingMs: 150,
    defaultPendingMinMs: 350,
  })

  if (typeof window !== 'undefined') {
    // The Google tag fires a native `page_view` for the initial document load
    // (send_page_view = true). To avoid double-counting page one, we emit
    // `spa_page_view` only for *client-side* navigations — i.e. skip the very
    // first resolve. The dedupe-on-initial-href guard makes this deterministic
    // regardless of whether onResolved happens to fire during hydration.
    // GTM: custom-event trigger `spa_page_view` -> a GA4 `page_view` event tag.
    let initialHandled = false
    let lastTrackedPath: string | null = null
    const initialHref = window.location.href
    router.subscribe('onResolved', ({ toLocation }) => {
      const path = toLocation.pathname + toLocation.searchStr
      // Skip the initial resolve — the Google tag already counts the document
      // load natively (send_page_view = true). Guarding on the initial href
      // makes this correct whether or not onResolved fires during hydration.
      if (!initialHandled) {
        initialHandled = true
        if (window.location.href === initialHref) {
          lastTrackedPath = path
          return
        }
      }
      // Ignore re-resolves that don't change the path — e.g. hash-only changes
      // from same-page anchor links (`#kategori-…`) — which would otherwise
      // double-count the pageview.
      if (path === lastTrackedPath) return
      lastTrackedPath = path
      track('spa_page_view', {
        page_path: path,
        page_location: window.location.href,
        page_title: document.title,
      })
    })

    // Turn plain internal `<a href>` clicks into client-side navigations so the
    // whole site behaves as an SPA — instant transitions instead of a ~2s SSR
    // round-trip on every click — without rewriting every anchor into <Link>.
    // SSR still renders real `<a href>` (crawlable), and per-anchor onClick
    // tracking handlers still run before this fires.
    document.addEventListener('click', (event) => {
      if (event.defaultPrevented || event.button !== 0) return
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
      const anchor = (event.target as HTMLElement | null)?.closest?.('a')
      if (!anchor) return
      const target = anchor.getAttribute('target')
      if (target && target !== '_self') return // new tab/window
      if (anchor.hasAttribute('download')) return
      if (anchor.getAttribute('rel')?.includes('external')) return
      let url: URL
      try {
        url = new URL(anchor.href, window.location.href)
      } catch {
        return
      }
      if (url.origin !== window.location.origin) return // external / wa.me / mailto / tel
      // Same-page hash links: let the browser scroll natively.
      if (url.pathname === window.location.pathname && url.search === window.location.search && url.hash) return
      event.preventDefault()
      void router.navigate({
        to: url.pathname,
        search: Object.fromEntries(url.searchParams),
        hash: url.hash ? url.hash.slice(1) : undefined,
      } as never)
    })
  }

  return router
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof getRouter>
  }
}
